
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCharacters } from '@/features/characters';
import { useLocations } from '@/features/locations';
import { useOrganizations } from '@/features/organizations';
import { useResponsiveValue } from '@/hooks/use-responsive-value';


const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export function RelationshipsSection() {
  const { t } = useTranslation();
  const { control, watch, setValue, getValues } = useFormContext();
  const resolveResponsive = useResponsiveValue();

  const { characters } = useCharacters();
  const { organizations } = useOrganizations();
  const { locations } = useLocations();

  const charOptions = characters.map(c => ({ id: c.id, label: c.name }));
  const orgOptions = organizations.map(o => ({ id: o.id, label: o.name }));
  const locOptions = locations.map(l => ({ id: l.id, label: l.name }));

  // Auto-add primary location to locationIds when it changes
  const primaryLocationId = watch('primaryLocationId');
  useEffect(() => {
    if (primaryLocationId) {
      const currentLocationIds = getValues('locationIds') || [];
      if (!currentLocationIds.includes(primaryLocationId)) {
        setValue('locationIds', [...currentLocationIds, primaryLocationId], {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [primaryLocationId, setValue, getValues]);

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('entity_form.events.involved_parties_title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('entity_form.events.involved_parties_desc')}
      </Typography>

      <Grid container spacing={3}>
        {/* Characters */}
        <Grid size={resolveResponsive({ xs: 12 })}>
          <Controller
            control={control}
            name="characterIds"
            defaultValue={[]}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={charOptions}
                disableCloseOnSelect
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={charOptions.filter(opt => (field.value || []).includes(opt.id))}
                onChange={(_, newValue) => {
                  field.onChange(newValue.map(v => v.id));
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('entity_form.events.characters_label')}
                    placeholder={t('entity_form.events.select_characters_placeholder')}
                    variant="filled"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip variant="outlined" label={option.label} key={key} {...tagProps} />;
                  })
                }
              />
            )}
          />
        </Grid>

        {/* Organizations */}
        <Grid size={resolveResponsive({ xs: 12 })}>
          <Controller
            control={control}
            name="organizationIds"
            defaultValue={[]}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={orgOptions}
                disableCloseOnSelect
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={orgOptions.filter(opt => (field.value || []).includes(opt.id))}
                onChange={(_, newValue) => {
                  field.onChange(newValue.map(v => v.id));
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('entity_form.events.organizations_label')}
                    placeholder={t('entity_form.events.select_organizations_placeholder')}
                    variant="filled"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip variant="outlined" label={option.label} key={key} {...tagProps} />;
                  })
                }
              />
            )}
          />
        </Grid>

        {/* Locations */}
        <Grid size={resolveResponsive({ xs: 12 })}>
          <Controller
            control={control}
            name="locationIds"
            defaultValue={[]}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={locOptions}
                disableCloseOnSelect
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={locOptions.filter(opt => (field.value || []).includes(opt.id))}
                onChange={(_, newValue) => {
                  field.onChange(newValue.map(v => v.id));
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('entity_form.events.locations_label')}
                    placeholder={t('entity_form.events.select_locations_placeholder')}
                    variant="filled"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip variant="outlined" label={option.label} key={key} {...tagProps} />;
                  })
                }
              />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
